import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { apiClient, showToast } from '../../utils/apiClient';
import { API } from '../../config';
import { formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek, formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert, formatDatetimeForInput, parseUTCDatetime, getTodayInAEST, isPrepaymentApproaching, hasAppointments, getAppointmentSummary } from '../../utils/helpers';

const QRScannerModal = ({ onClose, onPetScanned }) => {
  const scannerRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get available cameras
    const getCameras = async () => {
      try {
        console.log('🔍 Getting available cameras...');
        
        // Request camera permission first
        await navigator.mediaDevices.getUserMedia({ video: true });
        
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        
        console.log('📹 Found cameras:', videoDevices.length);
        videoDevices.forEach(cam => console.log('  -', cam.label || cam.deviceId));
        
        setCameras(videoDevices);
        
        // Prefer back camera on mobile
        const backCamera = videoDevices.find(device => 
          device.label.toLowerCase().includes('back') || 
          device.label.toLowerCase().includes('rear') ||
          device.label.toLowerCase().includes('environment')
        );
        
        const cameraToUse = backCamera?.deviceId || videoDevices[0]?.deviceId;
        console.log('📷 Selected camera:', cameraToUse);
        setSelectedCamera(cameraToUse);
        setLoading(false);
      } catch (err) {
        console.error('❌ Error getting cameras:', err);
        setError(`Unable to access camera: ${err.message}. Please allow camera access in your browser settings.`);
        setLoading(false);
      }
    };

    getCameras();
  }, []);

  useEffect(() => {
    if (!selectedCamera || scanning || loading) return;

    const startScanning = async () => {
      setScanning(true);
      setError('');
      console.log('🎬 Starting scanner with camera:', selectedCamera);

      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        const scanner = new Html5Qrcode('qr-reader');
        scannerRef.current = scanner;

        console.log('📸 Starting camera...');
        await scanner.start(
          selectedCamera,
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
          },
          async (decodedText) => {
            // Successfully scanned QR code
            console.log('✅ QR Code scanned:', decodedText);
            
            try {
              // Stop scanner first (safely)
              try {
                await scanner.stop();
                console.log('🛑 Scanner stopped after scan');
              } catch (stopErr) {
                console.warn('⚠️ Scanner stop warning (ignored):', stopErr.message);
              }
              
              // Fetch pet data using the QR code ID
              const response = await axios.get(`${API}/qr-scan/${decodedText}`);
              console.log('🐾 Pet data retrieved:', response.data.pet_name);
              onPetScanned(response.data);
            } catch (err) {
              console.error('❌ Error fetching pet data:', err);
              setError(`Pet not found with QR code: ${decodedText}`);
              // Restart scanner on error
              try {
                await scanner.stop();
              } catch (stopErr) {
                console.warn('⚠️ Scanner stop warning (ignored):', stopErr.message);
              }
              setScanning(false);
            }
          },
          (errorMessage) => {
            // Scanning error (can be ignored for most cases)
            // These are continuous scan attempts, not actual errors
          }
        );
        
        console.log('✅ Camera started successfully');
      } catch (err) {
        console.error('❌ Error starting scanner:', err);
        setError(`Failed to start camera: ${err.message}`);
        setScanning(false);
      }
    };

    startScanning();

    return () => {
      console.log('🧹 Cleaning up scanner');
      if (scannerRef.current) {
        // Safely stop scanner with multiple safety checks
        const scanner = scannerRef.current;
        
        try {
          // Check if scanner has getState method and is actually running
          if (typeof scanner.getState === 'function') {
            const state = scanner.getState();
            console.log('Scanner state:', state, '(1=SCANNING, 2=PAUSED, 3=NOT_STARTED)');
            
            // Only stop if state is 1 (SCANNING)
            if (state === 1) {
              scanner.stop()
                .then(() => console.log('✅ Scanner stopped successfully'))
                .catch(err => {
                  // Silently catch and log - don't let error propagate
                  console.warn('⚠️ Scanner stop error (ignored):', err.message);
                });
            } else {
              console.log('Scanner not in running state, skipping stop');
            }
          } else {
            // getState not available, try stopping anyway but catch errors
            console.log('getState not available, attempting stop anyway');
            scanner.stop()
              .then(() => console.log('✅ Scanner stopped successfully'))
              .catch(err => {
                console.warn('⚠️ Scanner stop error (ignored):', err.message);
              });
          }
        } catch (err) {
          // Catch any synchronous errors from getState or stop
          console.warn('⚠️ Scanner cleanup error (ignored):', err.message);
        }
      }
    };
  }, [selectedCamera, scanning, loading, onPetScanned]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-lg w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">📷 Scan QR Code</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {loading && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded mb-4">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-700 mr-3"></div>
              <span>Starting camera...</span>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            <p className="font-semibold mb-2">Camera Error</p>
            <p className="text-sm mb-3">{error}</p>
            <div className="text-xs">
              <p className="mb-1"><strong>Tips:</strong></p>
              <ul className="list-disc ml-4 space-y-1">
                <li>Make sure you allowed camera access when prompted</li>
                <li>Check browser settings → Site settings → Camera</li>
                <li>Try refreshing the page and allowing camera access</li>
                <li>On mobile, make sure no other app is using the camera</li>
              </ul>
            </div>
            <button
              onClick={() => {
                setError('');
                setLoading(true);
                setScanning(false);
                window.location.reload();
              }}
              className="mt-3 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm"
            >
              Reload & Try Again
            </button>
          </div>
        )}

        {cameras.length > 1 && !loading && !error && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Camera:
            </label>
            <select
              value={selectedCamera || ''}
              onChange={(e) => {
                setScanning(false);
                setSelectedCamera(e.target.value);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              {cameras.map(camera => (
                <option key={camera.deviceId} value={camera.deviceId}>
                  {camera.label || `Camera ${camera.deviceId.slice(0, 8)}`}
                </option>
              ))}
            </select>
          </div>
        )}

        <div 
          id="qr-reader" 
          className="w-full bg-gray-100 rounded-lg overflow-hidden" 
          style={{ minHeight: '300px' }}
        ></div>

        {!error && !loading && (
          <div className="mt-4 text-sm text-gray-600">
            <p>📱 Point your camera at the QR code on the pet's label</p>
            <p className="mt-2">🏷️ Make sure the QR code is well-lit and in focus</p>
            <p className="mt-2 text-xs text-gray-500">
              💡 The scanner will automatically detect and scan the QR code
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// =======================
// QUICK SCAN VIEW MODAL COMPONENT
// =======================

export default QRScannerModal;
